// components/PurchasingIndividualScorecardTabSet.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Tabs, Text, Anchor, Group } from '@mantine/core';
import { useSearchParams } from 'react-router-dom';
import CalendarMasterDetail from '../../../CalendarMasterDetail/CalendarMasterDetail';
import '@fontsource/orbitron/700.css';
import useAuthenticatedData from '../../../../hooks/useAuthenticatedData';
import config from '../../../../config.js';
import PurchasingIndividualPerformancePanel from './PurchasingIndividualPerformancePanel.jsx';
import PurchasingUserSelect from './PurchasingUserSelect.jsx';
import RFQDayDetails from './calendar/RFQDayDetails.js';
import MembershipPopup from '../MembershipPopup.js';
import SQLEditorPopup from '../SQLEditorPopup.js';
import PODayDetails from './calendar/PODayDetails.js';
import CallTrackerDetails from './calendar/CallTrackerDetails.js';
import PriceUpdateDetails from './calendar/PriceUpdateDetails.js';

const PurchasingIndividualScorecardTabSet = ({ filter }) => {
  const [activeTab, setActiveTab] = useState('0');
  const [selectedUser, setSelectedUser] = useState(null);
  
  // State for technical pop-ups
  const [openMembership, setOpenMembership] = useState(false);
  const [openSummaryQuery, setOpenSummaryQuery] = useState(false);
  
  const [searchParams] = useSearchParams();
  const defaultUserCode = searchParams.get('user');
  const defaultTab = searchParams.get('tab');

  // Load individual activity data - include filter in the URL or request
  const shouldFetchCalendarData = selectedUser && selectedUser.userCode && selectedUser.userCode.trim() !== '';
  
  // Construct the URL with filter parameter if available
  const calendarDataUrl = useMemo(() => {
    if (!shouldFetchCalendarData) return null;
    
    let url = `${config.apiBaseUrl}/supply-chain/purchasing/individual/calendar/${selectedUser.userCode}`;
    
    // Add filter parameter if available
    if (filter && filter.purchasingScorecardOnly !== undefined) {
      const filterParam = filter.purchasingScorecardOnly ? 'IN' : 'OUT';
      url += `?filter=${filterParam}`;
    }
    
    return url;
  }, [shouldFetchCalendarData, selectedUser, filter]);
  
  const { data: individualData, isLoading: loading, error, refetch: loadIndividualData } = useAuthenticatedData(
    calendarDataUrl
  );

  // Set the initial active tab based on the 'tab' parameter in the URL
  useEffect(() => {
    if (defaultTab) {
      const tabIndex = parseInt(defaultTab, 10);
      if (!isNaN(tabIndex) && tabIndex >= 0 && tabIndex <= 4) {
        setActiveTab(tabIndex.toString());
      }
    }
  }, [defaultTab]);

  // KEY CHANGE: Add a useEffect to reload data when the tab is active, user is selected, or filter changes
  // This ensures the data is fresh and triggers a re-render
  useEffect(() => {
    if (activeTab === '1' && shouldFetchCalendarData) {
      loadIndividualData();
    }
  }, [activeTab, shouldFetchCalendarData, filter, loadIndividualData]); // Added filter as dependency

  const handleTabChange = (newValue) => {
    setActiveTab(newValue);
    // Always load the data when switching to the activity tab
    if (newValue === '1' && shouldFetchCalendarData) {
      loadIndividualData();
    }
  };

  const handleUserSelect = (user) => {
    console.log('User selected in TabSet:', user);
    setSelectedUser(user);
  };

  // Activities configuration for individual-level data
  const individualActivitiesConfig = useMemo(() => [
    {
      type: 'RFQs',
      color: '#1976d2',
      dataFields: {
        docCount: 'totalRFQDocCount',
        lineCount: 'totalRFQLineCount',
        total: 'totalRFQDollars',
      },
      minimal: false,
      weeklyAverage: true,
      monthlyAverage: true,
      detailView: (props) => <RFQDayDetails {...props} filter={filter} />,
    },
    {
      type: 'POs',
      color: '#2D875D',
      dataFields: {
        docCount: 'poDocCount',
        lineCount: 'poLineCount',
        total: 'poDollars',
      },
      minimal: false,
      weeklyAverage: true,
      monthlyAverage: true,
      detailView: (props) => <PODayDetails {...props} filter={filter} />,
    },
    {
      type: 'Price Updates',
      color: '#A23E48',
      dataFields: {
        lineCount: 'sellPricesUpdated',
      },
      minimal: true,
      weeklyAverage: false,
      monthlyAverage: false,
      detailView: (props) => <PriceUpdateDetails {...props} filter={filter} />,
    },
    {
      type: 'Call Trackers',
      color: '#9C4EE5',
      dataFields: {
        lineCount: 'callTrackerDocCount',
      },
      minimal: true,
      weeklyAverage: false,
      monthlyAverage: false,
      detailView: (props) => <CallTrackerDetails {...props} filter={filter} />,
    },
  ], [filter]); // Added filter as dependency

  // Format the individual data as needed for CalendarMasterDetail
  const formattedIndividualData = useMemo(() => {
    if (!individualData) return [];
    
    console.log("Processing individual data for calendars:", individualData);
    
    return individualData.individualCalendarActivity.map((item) => ({
      date: item.date,
      ...item, // Spread all fields so that CalendarMasterDetail can access them via the keys defined in individualActivitiesConfig
    }));
  }, [individualData]);

  // KEY CHANGE: Add a key prop that changes when data or filter changes to force re-render
  const calendarKey = `calendar-${individualData ? individualData.individualCalendarActivity.length : 'empty'}-${filter ? JSON.stringify(filter) : 'no-filter'}`;

  return (
    <Box style={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* User Title and Select */}
      <Box
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          paddingTop: 0,
          paddingLeft: 0,
          borderBottom: '1px solid var(--mantine-color-gray-3)',
        }}
      >
        <Text
          style={{
            fontFamily: 'Orbitron, sans-serif',
            color: '#FDB813',
            fontStyle: 'italic',
            fontSize: '1.5rem',
          }}
        >
          {selectedUser
            ? `${selectedUser.fullName}`
            : 'No User Selected'}
        </Text>
        <PurchasingUserSelect
          onUserSelect={handleUserSelect}
          defaultUserCode={defaultUserCode}
        />
      </Box>

      {/* Tabs */}
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: '1px solid var(--mantine-color-gray-3)',
        }}
      >
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tabs.List>
            <Tabs.Tab value="0">Scorecard</Tabs.Tab>
            {/* <Tabs.Tab value="1">RFQs, POs, Price Updates, Call Trackers</Tabs.Tab> */}
          </Tabs.List>
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box style={{ flexGrow: 1, overflow: 'hidden', padding: 0, height: '100%' }}>
        {activeTab === '0' && (
          <PurchasingIndividualPerformancePanel 
            selectedUser={selectedUser} 
            filter={filter}
          />
        )}
        
        {activeTab === '1' && (
          <>
            {/* Technical options: small links for debugging */}
            <Group mb="sm" gap="sm">
              <Anchor
                size="xs"
                c="dimmed"
                onClick={() => setOpenMembership(true)}
                style={{ cursor: 'pointer' }}
              >
                Who is Included?
              </Anchor>
              <Anchor
                size="xs"
                c="dimmed"
                onClick={() => setOpenSummaryQuery(true)}
                style={{ cursor: 'pointer' }}
              >
                Calendar Summary Query
              </Anchor>
            </Group>

            {selectedUser ? (
              loading ? (
                <Text p="md">
                  Loading Individual Data...
                </Text>
              ) : error ? (
                <Text p="md" c="red">
                  Error loading individual data.
                </Text>
              ) : (
                <CalendarMasterDetail
                  key={calendarKey} // KEY CHANGE: Add key prop to force re-render when filter changes
                  businessKey={selectedUser.userCode}
                  data={formattedIndividualData}
                  currentYear={new Date().getFullYear()}
                  currentMonth={new Date().getMonth()}
                  onMonthChange={(year, month) => {
                    console.log(`Month changed to: ${month + 1}/${year}`);
                    // KEY CHANGE: Reload data when month changes
                    if (shouldFetchCalendarData) {
                      loadIndividualData();
                    }
                  }}
                  activities={individualActivitiesConfig}
                  filter={filter} // Pass filter to CalendarMasterDetail
                />
              )
            ) : (
              <Text p="md">
                Please select a purchasing user to view activity data.
              </Text>
            )}
          </>
        )}
      </Box>

      {/* Pop-up dialogs */}
      <MembershipPopup opened={openMembership} onClose={() => setOpenMembership(false)} />
      <SQLEditorPopup
        opened={openSummaryQuery}
        onClose={() => setOpenSummaryQuery(false)}
        title="Summary Query"
        endpoint={`${config.apiBaseUrl}/supply-chain/purchasing/individual/scorecard/queries/calendar/calendar-summary-query`}
      />
    </Box>
  );
};

export default PurchasingIndividualScorecardTabSet;